import _object_spread from "@swc/helpers/src/_object_spread.mjs";
import _object_spread_props from "@swc/helpers/src/_object_spread_props.mjs";
import { eventChannel, buffers } from 'redux-saga';
import { select, call, takeEvery, all, fork, take } from 'redux-saga/effects';
import isSSR from '../../utils/isSSR';
import biLogger from '@wix/da-bi/pkg/BiLogger.logger';
import { PureBiData } from '@wix/da-bi/pkg/events';
import { logPageView } from './actions';
import { getPublicSession } from '../../publicSession/selectors';
import { getBiContextData } from './selectors';
import isFinite from 'lodash/isFinite';
import isNumber from 'lodash/isNumber';
import ddt from '@wix/da-ddt';
// helper for logging events from sagas
export function* logBiEvent(data, isPageView = false) {
    const contextData = yield select(isPageView ? getPublicSession : getBiContextData);
    yield call([
        biLogger,
        'log'
    ], contextData, data);
}
function* handleLogPageView({ payload  }) {
    if (isSSR()) {
        return;
    }
    const sessionData = yield select(getPublicSession);
    if (payload.useNg === true) {
        var _payload_metadata;
        yield call(logBiEvent, PureBiData(_object_spread_props(_object_spread({
            evid: 9,
            screen_width: window.innerWidth,
            screen_height: window.innerHeight
        }, payload), {
            metadata: makeBiMetadataForAds((_payload_metadata = payload.metadata) !== null && _payload_metadata !== void 0 ? _payload_metadata : '', sessionData)
        })), true);
    } else {
        var _payload_metadata1;
        yield call(logBiEvent, PureBiData(_object_spread_props(_object_spread({
            evid: 99,
            screen_width: window.innerWidth,
            screen_height: window.innerHeight
        }, payload), {
            metadata: makeBiMetadataForAds((_payload_metadata1 = payload.metadata) !== null && _payload_metadata1 !== void 0 ? _payload_metadata1 : '', sessionData)
        })), true);
    }
}
function makeBiMetadataForAds(currentMetadata, session) {
    if (isSSR() || !session.adsEnabled) {
        return currentMetadata;
    }
    // Having to do this parsing sucks but most of the time pageviews do not set
    // any metadata so most fo the time this is skipped
    let current = [];
    try {
        if (currentMetadata.startsWith('[') || currentMetadata.startsWith('{')) {
            current = JSON.parse(currentMetadata);
        }
    } catch (e) {
        ddt.alert('ads', 'Failed to parse metadata for pv', [
            'ads'
        ], {});
    }
    // Convert bad metadata into array, should always be array
    if (typeof current === 'object' && !Array.isArray(current)) {
        current = [
            current
        ];
    }
    // Find all ads on the page
    const unitList = document.querySelectorAll('[data-safety]');
    if (!unitList || unitList.length === 0) {
        current.push({
            type: 'ads',
            slots: {}
        });
        return JSON.stringify(current);
    }
    // Generate the ads packet
    const units = Array.from(unitList);
    if (units && Array.isArray(current)) {
        const slots = {};
        for (const el of units){
            if (el.getAttribute('data-safety') !== 'off') {
                slots[el.id] = {};
            }
        }
        current.push({
            type: 'ads',
            slots
        });
        return JSON.stringify(current);
    }
    return currentMetadata;
}
function makeBiData(beat, metrics) {
    var _window___APP_INFO__;
    const data = {
        evid: 1,
        // project
        appinf: (_window___APP_INFO__ = window['__APP_INFO__']) !== null && _window___APP_INFO__ !== void 0 ? _window___APP_INFO__ : 'unknown',
        beat
    };
    const metricMap = {
        FP: 'p_fp',
        FCP: 'p_fcp',
        FID: 'p_fid',
        INP: 'p_inp',
        LCP: 'p_lcp',
        CLS: 'p_cls',
        TTFB: 'nt_ttfb',
        fetchTime: 'nt_ft',
        workerTime: 'nt_wt',
        totalTime: 'nt_tt',
        downloadTime: 'nt_dt',
        headerSize: 'nt_hs',
        dnsLookupTime: 'nt_dlt',
        downlink: 'ni_dl',
        effectiveType: 'ni_et',
        rtt: 'ni_rtt',
        saveData: 'ni_sd'
    };
    for (const metric of metrics){
        // Record the vital bracket poor, needs improvement, good
        if (metric.vitalsScore) {
            data[`${metric.type}_vs`.toLocaleLowerCase()] = metric.vitalsScore;
        }
        // Record things with a duration only
        if (metric.duration && metricMap[metric.type]) {
            data[metricMap[metric.type]] = Math.round(metric.duration);
            continue;
        }
        // Walk the data object and extract stuff
        if (metric.data) {
            const keys = Object.keys(metric.data);
            if (keys.length > 0) for (const key of keys){
                if (!metricMap[key]) {
                    continue; // skip missing
                }
                if (isFinite(metric.data[key])) {
                    data[metricMap[key]] = Math.round(metric.data[key]);
                } else {
                    data[metricMap[key]] = metric.data[key];
                }
            }
        }
    }
    return PureBiData(data);
}
function* handlePerfLogging() {
    if (typeof window === 'undefined') {
        return;
    }
    let Perfume;
    try {
        Perfume = yield import('perfume.js');
    } catch (e) {
        return;
    }
    const perfumeChannel = eventChannel((emitter)=>{
        new Perfume.default({
            logging: false,
            dataConsumption: false,
            networkInformation: true,
            resourceTiming: false,
            analyticsTracker: (log)=>emitter(log)
        });
        return ()=>{};
    }, buffers.expanding());
    let metrics = [];
    let fcpDone = false, ttfbDone = false;
    while(true){
        const log = yield take(perfumeChannel);
        const { metricName , data , rating: vitalsScore  } = log;
        ddt.log('perfume', 'PerfData', {
            metricName,
            data,
            vitalsScore
        });
        let duration;
        if (isNumber(data)) {
            duration = data;
        }
        switch(metricName){
            case 'navigationTiming':
                metrics.push({
                    type: metricName,
                    data,
                    vitalsScore
                });
                break;
            case 'networkInformation':
                metrics.push({
                    type: metricName,
                    data
                });
                break;
            case 'FCP':
                fcpDone = true;
                metrics.push({
                    type: metricName,
                    duration,
                    vitalsScore
                });
                if (ttfbDone) {
                    yield call(logBiEvent, makeBiData('fcp', metrics));
                }
                break;
            case 'TTFB':
                ttfbDone = true;
                metrics.push({
                    type: metricName,
                    duration,
                    vitalsScore
                });
                if (fcpDone) {
                    yield call(logBiEvent, makeBiData('fcp', metrics));
                }
                break;
            default:
                metrics.push({
                    type: metricName,
                    duration,
                    vitalsScore
                });
                if (metricName === 'LCP') {
                    yield call(logBiEvent, makeBiData('lcp', metrics));
                    metrics = [];
                }
                break;
        }
    }
}
export default function* rootSaga() {
    yield fork(handlePerfLogging);
    yield all([
        takeEvery(logPageView, handleLogPageView)
    ]);
}
